// src/pages/PublicArticlesListPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { dailyHelpService } from '../Services/dailyHelpService';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Search,
  Loader2,
  Sparkles,
  Baby,
  Puzzle,
  BookOpen,
  Star,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  FileSearch,
  Phone,
} from 'lucide-react';
import SEO from '../components/SEO';

const LOGO_URL = '/RemaH.png';

// Catégories
const categories = [
  {
    id: 'petite-enfance',
    title: 'Petite Enfance',
    age: '0-2 ans',
    icon: Baby,
    bgColor: 'bg-pink-500',
  },
  { id: 'enfance', title: 'Enfance', age: '3-5 ans', icon: Puzzle, bgColor: 'bg-blue-500' },
  { id: 'preado', title: 'Préado', age: '6-12 ans', icon: BookOpen, bgColor: 'bg-green-500' },
  {
    id: 'adolescence',
    title: 'Adolescence',
    age: '13-18 ans',
    icon: Star,
    bgColor: 'bg-indigo-500',
  },
];

const getCategoryInfo = (article) => {
  const tags = (article.tags || []).join(' ').toLowerCase();
  if (tags.includes('0-2')) return categories[0];
  if (tags.includes('3-5')) return categories[1];
  if (tags.includes('6-12')) return categories[2];
  if (tags.includes('13-18')) return categories[3];
  return categories[1];
};

const ageFilters = ['Tous', '0-2 ans', '3-5 ans', '6-12 ans', '13-18 ans'];

const PublicArticlesListPage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAge, setSelectedAge] = useState('Tous');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const ARTICLES_PER_PAGE = 6;

  useEffect(() => {
    loadArticles();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedAge, searchQuery]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const data = await dailyHelpService.getPublishedArticles();
      setArticles(data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrage
  const filteredArticles = articles.filter((article) => {
    if (selectedAge !== 'Tous') {
      const tags = (article.tags || []).join(' ').toLowerCase();
      if (!tags.includes(selectedAge.toLowerCase().replace(' ans', ''))) return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const inTitle = (article.title || '').toLowerCase().includes(q);
      const inDesc = (article.description || '').toLowerCase().includes(q);
      const inTags = (article.tags || []).some((t) => t.toLowerCase().includes(q));
      if (!inTitle && !inDesc && !inTags) return false;
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE);
  const visibleArticles = filteredArticles.slice(
    currentPage * ARTICLES_PER_PAGE,
    (currentPage + 1) * ARTICLES_PER_PAGE
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title="Articles Santé"
        description="Conseils santé pour toute la famille : petite enfance, enfance, préadolescence et adolescence. Articles vulgarisés et accessibles."
        path="/articles"
        keywords="articles santé enfants, conseils santé adolescents, 0-2 ans, 3-5 ans, 6-12 ans, 13-18 ans, santé famille"
      />

      <PublicNav />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors mb-6"
        >
          ← Retour
        </button>
        {/* Header avec logo */}
        <div className="text-center mb-10">
          <img
            src="/RemaH.png"
            alt="Remadia"
            className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-4 object-contain"
          />
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3">
            Articles Santé
          </h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
            Conseils et ressources pour toute la famille, classés par tranche d'âge
          </p>
        </div>

        {/* Recherche */}
        <div className="max-w-lg mx-auto mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un article..."
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 dark:focus:ring-cyan-900/30 text-gray-900 dark:text-gray-100 placeholder-gray-400"
            />
          </div>
        </div>

        {/* Filtres par âge */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {ageFilters.map((age) => (
            <button
              key={age}
              onClick={() => setSelectedAge(age)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedAge === age
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                  : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-cyan-400'
              }`}
            >
              {age}
            </button>
          ))}
        </div>

        {/* Compteur + pagination */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredArticles.length} article{filteredArticles.length > 1 ? 's' : ''}
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center disabled:opacity-30 transition-all"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[3rem] text-center">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center disabled:opacity-30 transition-all"
              >
                <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          )}
        </div>

        {/* Articles */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
          </div>
        ) : filteredArticles.length === 0 ? (
          <Card className="p-12 text-center bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
            <p className="text-gray-500 dark:text-gray-400">Aucun article trouvé.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {visibleArticles.map((article) => {
              const categoryInfo = getCategoryInfo(article);
              const Icon = categoryInfo.icon;
              const imageUrl = article.thumbnail || article.images?.[0];

              return (
                <Card
                  key={article.id}
                  className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-all cursor-pointer rounded-xl group overflow-hidden"
                  onClick={() => navigate(`/articles/${article.id}`)}
                >
                  <div className="flex h-full">
                    <div className="flex-1 p-5 flex flex-col justify-between min-w-0">
                      <div>
                        <div
                          className={`inline-flex w-7 h-7 ${categoryInfo.bgColor} rounded-lg items-center justify-center shadow-md mb-2`}
                          title={categoryInfo.title}
                        >
                          <Icon className="w-3.5 h-3.5 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-cyan-500 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                          {article.description}
                        </p>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {article.tags?.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-md"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="w-36 sm:w-44 flex-shrink-0">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center">
                          <Icon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* CTA Inscription */}
        <Card className="mt-12 p-8 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 border-2 border-cyan-200 dark:border-cyan-800 text-center">
          <Sparkles className="w-10 h-10 text-cyan-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Découvrez toutes les fonctionnalités Remadia
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-lg mx-auto">
            Chat IA santé, analyse de prise de sang, recherche de médicaments et plus encore.
          </p>
          <Button
            onClick={() => navigate('/auth')}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-3 rounded-xl text-lg"
          >
            Créer un compte gratuit
            <Sparkles className="w-5 h-5 ml-2" />
          </Button>
        </Card>

        {/* Fonctionnalités */}
        <div className="grid md:grid-cols-3 gap-4 mt-6 mb-12">
          <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <MessageCircle className="w-8 h-8 text-blue-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                Chat IA Santé
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">Posez vos questions santé</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <FileSearch className="w-8 h-8 text-red-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                Analyse de Sang
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">Comprenez vos résultats</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <Phone className="w-8 h-8 text-green-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Urgences</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">Numéros et défibrillateurs</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// ─── Navbar publique ────────────────────────────────────
const PublicNav = () => {
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 px-4 sm:px-6 lg:px-10 py-3 bg-white/80 dark:bg-gray-950/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <Link to="/articles" className="flex items-center gap-2">
          <img src="/RemaH.png" alt="Remadia" className="w-9 h-9 object-contain" />
          <span className="text-lg font-bold text-gray-900 dark:text-white">REMADIA HEALTH</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/articles"
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors hidden sm:block"
          >
            Articles
          </Link>
          <Button
            onClick={() => navigate('/auth')}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-5 py-2 rounded-xl text-sm font-semibold"
          >
            <span className="hidden sm:inline">Connexion / Inscription</span>
            <span className="sm:hidden">Connexion</span>
          </Button>
        </div>
      </div>
    </nav>
  );
};

export default PublicArticlesListPage;
